// hooks/useAutoAudioRecorder.js
import { useRef, useState, useEffect } from "react";
import apiClient from "../utils/apiClient";

const MAX_RECORD_TIME_MS = 120000; // 2 minutes safety cap
const SILENCE_DURATION_MS = 3000; // 3 seconds of silence to auto-stop
const SILENCE_THRESHOLD = 5; // Amplitude threshold (less sensitive than before)
const MIN_SPEECH_DURATION_MS = 2000; // Must have at least 2s of speech before auto-stop

export default function useAutoAudioRecorder({ onTranscribed, mockInterviewId, enabled }) {
  const mediaRecorderRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const chunksRef = useRef([]);
  const [recording, setRecording] = useState(false);
  const maxRecordTimeoutRef = useRef(null);
  const audioContextRef = useRef(null);

  useEffect(() => {
    if (enabled && !recording) {
      start();
    }
    // eslint-disable-next-line
  }, [enabled]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new window.MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      setRecording(true);

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mediaRecorderRef.current.onstop = () => {
        setRecording(false);
        const audioBlob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (audioBlob.size > 1000) {
          sendToServer(audioBlob);
        } else {
          onTranscribed({ error: "Audio was too short or empty." });
        }
        clearTimeout(maxRecordTimeoutRef.current);
      };
      mediaRecorderRef.current.start();

      // Safety: Max record time cap
      maxRecordTimeoutRef.current = setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
          mediaRecorderRef.current.stop();
        }
      }, MAX_RECORD_TIME_MS);

      // Silence detection setup
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);

      const bufferLength = analyser.fftSize;
      const dataArray = new Uint8Array(bufferLength);
      let speechStarted = false;
      let speechDuration = 0;

      function checkSilence() {
        analyser.getByteTimeDomainData(dataArray);
        const maxAmplitude = Math.max(...Array.from(dataArray).map((v) => Math.abs(v - 128)));
        const isSilent = maxAmplitude < SILENCE_THRESHOLD;

        if (!isSilent) {
          speechStarted = true;
          speechDuration += 16; // ~16ms per frame at 60fps
          if (silenceTimerRef.current) {
            clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
          }
        } else if (speechStarted && speechDuration >= MIN_SPEECH_DURATION_MS) {
          // Only start silence timer after student has actually spoken for at least 2s
          if (!silenceTimerRef.current) {
            silenceTimerRef.current = setTimeout(() => {
              if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
                mediaRecorderRef.current.stop();
              }
              if (audioContext.state !== 'closed') audioContext.close().catch(() => {});
            }, SILENCE_DURATION_MS);
          }
        }
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
          requestAnimationFrame(checkSilence);
        }
      }
      requestAnimationFrame(checkSilence);

      // Listen for manual "done speaking" event from the UI
      const handleDoneSpeaking = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
          mediaRecorderRef.current.stop();
        }
        if (audioContext.state !== 'closed') audioContext.close().catch(() => {});
      };
      window.addEventListener('mock-interview-done-speaking', handleDoneSpeaking);
      // Store cleanup reference
      mediaRecorderRef.current._cleanupDoneSpeaking = () => {
        window.removeEventListener('mock-interview-done-speaking', handleDoneSpeaking);
      };
    } catch (err) {
      setRecording(false);
      onTranscribed({ error: "Mic error or permission denied." });
    }
  };

  const sendToServer = async (audioBlob) => {
    // Signal component to show thinking state immediately
    onTranscribed({ _thinking: true });
    try {
      const formData = new FormData();
      formData.append("audio", audioBlob, "answer.webm");
      formData.append("mockInterviewId", mockInterviewId);

      const { data } = await apiClient.post(
        "/mock-interview/answer-audio",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      onTranscribed(data);
    } catch (err) {
      onTranscribed({ error: "Failed to send audio to server." });
    }
  };

  // Expose manual stop
  const stop = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(() => {});
    }
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current) {
        if (mediaRecorderRef.current._cleanupDoneSpeaking) {
          mediaRecorderRef.current._cleanupDoneSpeaking();
        }
        if (mediaRecorderRef.current.state === "recording") {
          mediaRecorderRef.current.stop();
        }
      }
      if (maxRecordTimeoutRef.current) clearTimeout(maxRecordTimeoutRef.current);
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    };
  }, []);

  return { recording, stop };
}
